import JSZip from 'jszip';
/**
 * Generates minimal valid OOXML bytes for blank documents, client-side. This
 * lets "New docx/xlsx/pptx" work without shipping binary templates — the
 * frontend produces a well-formed empty file on demand and uploads it via
 * CreateDocument. ONLYOFFICE opens each output cleanly.
 *
 * The content lives here (not on the server) so shirohige stays
 * format-agnostic — it just stores whatever bytes we hand it.
 */ // ---------- Shared helpers ----------
const CONTENT_TYPES_BASE = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
    '<Default Extension="xml" ContentType="application/xml"/>'
].join('');
const ROOT_RELS = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="{DOC}"/>',
    '</Relationships>'
].join('');
async function zipToBytes(zip) {
    const blob = await zip.generateAsync({
        type: 'uint8array',
        compression: 'DEFLATE'
    });
    return blob;
}
// ---------- DOCX ----------
function buildDocx() {
    const zip = new JSZip();
    zip.file('[Content_Types].xml', CONTENT_TYPES_BASE + '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' + '</Types>');
    zip.file('_rels/.rels', ROOT_RELS.replace('{DOC}', 'word/document.xml'));
    zip.file('word/document.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">',
        '<w:body><w:p/></w:body>',
        '</w:document>'
    ].join(''));
    return zipToBytes(zip);
}
// ---------- XLSX ----------
function buildXlsx() {
    const zip = new JSZip();
    zip.file('[Content_Types].xml', CONTENT_TYPES_BASE + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' + '</Types>');
    zip.file('_rels/.rels', ROOT_RELS.replace('{DOC}', 'xl/workbook.xml'));
    zip.file('xl/_rels/workbook.xml.rels', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>',
        '</Relationships>'
    ].join(''));
    zip.file('xl/workbook.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
        '<sheets><sheet name="Sheet1" sheetId="1" r:id="rId1"/></sheets>',
        '</workbook>'
    ].join(''));
    zip.file('xl/worksheets/sheet1.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
        '<sheetData/>',
        '</worksheet>'
    ].join(''));
    return zipToBytes(zip);
}
// ---------- PPTX ----------
function buildPptx() {
    const zip = new JSZip();
    zip.file('[Content_Types].xml', CONTENT_TYPES_BASE + '<Override PartName="/ppt/presentation.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml"/>' + '<Override PartName="/ppt/slides/slide1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/>' + '<Override PartName="/ppt/slideLayouts/slideLayout1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slideLayout+xml"/>' + '<Override PartName="/ppt/slideMasters/slideMaster1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slideMaster+xml"/>' + '<Override PartName="/ppt/theme/theme1.xml" ContentType="application/vnd.openxmlformats-officedocument.theme+xml"/>' + '</Types>');
    zip.file('_rels/.rels', ROOT_RELS.replace('{DOC}', 'ppt/presentation.xml'));
    const R = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
    const P = 'http://schemas.openxmlformats.org/presentationml/2006/main';
    const A = 'http://schemas.openxmlformats.org/drawingml/2006/main';
    zip.file('ppt/_rels/presentation.xml.rels', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
        `<Relationship Id="rId1" Type="${R}/slide" Target="slides/slide1.xml"/>`,
        `<Relationship Id="rId2" Type="${R}/slideMaster" Target="slideMasters/slideMaster1.xml"/>`,
        `<Relationship Id="rId3" Type="${R}/theme" Target="theme/theme1.xml"/>`,
        '</Relationships>'
    ].join(''));
    zip.file('ppt/presentation.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        `<p:presentation xmlns:a="${A}" xmlns:r="${R}" xmlns:p="${P}">`,
        '<p:sldMasterIdLst><p:sldMasterId id="2147483648" r:id="rId2"/></p:sldMasterIdLst>',
        '<p:sldIdLst><p:sldId id="256" r:id="rId1"/></p:sldIdLst>',
        '<p:sldSz cx="9144000" cy="6858000"/>',
        '<p:notesSz cx="6858000" cy="9144000"/>',
        '</p:presentation>'
    ].join(''));
    // Slide 1
    zip.file('ppt/slides/_rels/slide1.xml.rels', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
        `<Relationship Id="rId1" Type="${R}/slideLayout" Target="../slideLayouts/slideLayout1.xml"/>`,
        '</Relationships>'
    ].join(''));
    zip.file('ppt/slides/slide1.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        `<p:sld xmlns:a="${A}" xmlns:r="${R}" xmlns:p="${P}">`,
        '<p:cSld><p:spTree>',
        '<p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>',
        '<p:grpSpPr/>',
        '</p:spTree></p:cSld>',
        '</p:sld>'
    ].join(''));
    // Slide layout
    zip.file('ppt/slideLayouts/_rels/slideLayout1.xml.rels', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
        `<Relationship Id="rId1" Type="${R}/slideMaster" Target="../slideMasters/slideMaster1.xml"/>`,
        '</Relationships>'
    ].join(''));
    zip.file('ppt/slideLayouts/slideLayout1.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        `<p:sldLayout xmlns:a="${A}" xmlns:r="${R}" xmlns:p="${P}" type="blank">`,
        '<p:cSld name="Blank"><p:spTree>',
        '<p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>',
        '<p:grpSpPr/>',
        '</p:spTree></p:cSld>',
        '</p:sldLayout>'
    ].join(''));
    // Slide master
    zip.file('ppt/slideMasters/_rels/slideMaster1.xml.rels', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
        `<Relationship Id="rId1" Type="${R}/slideLayout" Target="../slideLayouts/slideLayout1.xml"/>`,
        `<Relationship Id="rId2" Type="${R}/theme" Target="../theme/theme1.xml"/>`,
        '</Relationships>'
    ].join(''));
    zip.file('ppt/slideMasters/slideMaster1.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        `<p:sldMaster xmlns:a="${A}" xmlns:r="${R}" xmlns:p="${P}">`,
        '<p:cSld><p:bg><p:bgRef idx="1001"><a:schemeClr val="bg1"/></p:bgRef></p:bg><p:spTree>',
        '<p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>',
        '<p:grpSpPr/>',
        '</p:spTree></p:cSld>',
        '<p:sldLayoutIdLst><p:sldLayoutId id="2147483649" r:id="rId1"/></p:sldLayoutIdLst>',
        '</p:sldMaster>'
    ].join(''));
    // Minimal theme — ONLYOFFICE tolerates a sparse theme but requires it present.
    zip.file('ppt/theme/theme1.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        `<a:theme xmlns:a="${A}" name="Blank">`,
        '<a:themeElements>',
        '<a:clrScheme name="Blank">',
        '<a:dk1><a:sysClr val="windowText" lastClr="000000"/></a:dk1>',
        '<a:lt1><a:sysClr val="window" lastClr="FFFFFF"/></a:lt1>',
        '<a:dk2><a:srgbClr val="000000"/></a:dk2>',
        '<a:lt2><a:srgbClr val="FFFFFF"/></a:lt2>',
        '<a:accent1><a:srgbClr val="4472C4"/></a:accent1>',
        '<a:accent2><a:srgbClr val="ED7D31"/></a:accent2>',
        '<a:accent3><a:srgbClr val="A5A5A5"/></a:accent3>',
        '<a:accent4><a:srgbClr val="FFC000"/></a:accent4>',
        '<a:accent5><a:srgbClr val="5B9BD5"/></a:accent5>',
        '<a:accent6><a:srgbClr val="70AD47"/></a:accent6>',
        '<a:hlink><a:srgbClr val="0563C1"/></a:hlink>',
        '<a:folHlink><a:srgbClr val="954F72"/></a:folHlink>',
        '</a:clrScheme>',
        '<a:fontScheme name="Blank">',
        '<a:majorFont><a:latin typeface="Calibri Light"/><a:ea typeface=""/><a:cs typeface=""/></a:majorFont>',
        '<a:minorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/></a:minorFont>',
        '</a:fontScheme>',
        '<a:fmtScheme name="Blank">',
        '<a:fillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:fillStyleLst>',
        '<a:lnStyleLst><a:ln/><a:ln/><a:ln/></a:lnStyleLst>',
        '<a:effectStyleLst><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle></a:effectStyleLst>',
        '<a:bgFillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:bgFillStyleLst>',
        '</a:fmtScheme>',
        '</a:themeElements>',
        '</a:theme>'
    ].join(''));
    return zipToBytes(zip);
}
// ---------- Public API ----------
export async function generateBlankTemplate(type) {
    switch(type){
        case 'docx':
            return buildDocx();
        case 'xlsx':
            return buildXlsx();
        case 'pptx':
            return buildPptx();
    }
}
